import React, { useState, useEffect } from "react";
import {
    Search, RefreshCw, Cpu, Database, CheckCircle2,
    XCircle, ShieldCheck, ChevronLeft, ChevronRight,
    ChevronsLeft, ChevronsRight, Tag
} from "lucide-react";
import baseapi from "../../../services/baseapi"; // Adjust import path
import { showError } from "../../../utils/toast"; // Adjust import path
import { ItemsPerPageDropdown } from "../../../components/ItemsPerPageDropdown "; // Adjust import path

interface IloFirmware {
    id: number;
    created: string;
    updated: string;
    asset_id: number;
    item_type: string;
    firmware_item_id: string;
    name: string;
    version: string;
    updateable: boolean;
}

interface AssetIloFirmwareProps {
    assetId: string | number;
}

export default function AssetIloFirmware({ assetId }: AssetIloFirmwareProps) {
    const [firmwareList, setFirmwareList] = useState<IloFirmware[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [totalRecords, setTotalRecords] = useState(0);

    const fetchFirmware = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * limit;

            const queryObj: any = {
                asset_id: assetId
            };

            if (searchTerm) {
                // ILIKE search on the firmware name
                queryObj.name = { $ilike: `%${searchTerm}%` };
            }

            const payload: any = {
                skip,
                limit,
                query: JSON.stringify(queryObj)
            };

            const response = await baseapi.get("/asset_ilo_firmware", { params: payload });

            if (response.data.status) {
                setFirmwareList(response.data.data);
                setTotalRecords(response.data.total || response.data.data.length || 0);
            } else {
                showError(response.data.message || "Failed to load firmware data");
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (assetId) {
            fetchFirmware();
        }
    }, [currentPage, limit, assetId]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchFirmware();
    };

    const renderUpdateableBadge = (updateable: boolean) => {
        if (updateable) {
            return (
                <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 whitespace-nowrap">
                    <CheckCircle2 size={10} /> Yes
                </div>
            );
        }
        return (
            <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-gray-500/10 text-gray-200 border border-gray-500/20 whitespace-nowrap">
                <XCircle size={10} /> No
            </div>
        );
    };

    return (
        <div className="flex flex-col text-gray-200 relative overflow-hidden pb-4">
            {/* Toolbar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-3 border-b border-gray-700 pb-2">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                        <Cpu className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-white leading-tight">Firmware / BIOS</h2>
                        
                    </div>
                </div>
                
                <div className="flex items-center gap-2">
                    <form onSubmit={handleSearch} className="relative w-64 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-200 group-focus-within:text-purple-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search firmware..."
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-all"
                        />
                    </form>
                    <button
                        onClick={() => { setCurrentPage(1); fetchFirmware(); }}
                        disabled={loading}
                        className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0"
                    >
                        <RefreshCw size={14} className={loading ? 'animate-spin' : 'group-hover:text-purple-400'} />
                    </button>
                </div>
            </div>

            {/* Table Container */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden shadow-2xl flex flex-col max-h-full">
                <div className="relative overflow-x-auto overflow-y-auto custom-scroll">
                    <table className="w-full text-left border-collapse table-fixed">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-700/50">
                                <th className="w-[10%] px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <Tag size={12} className="text-gray-200 shrink-0" /> <span>Item ID</span>
                                    </div>
                                </th>
                                <th className="w-[40%] px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <Database size={12} className="text-purple-400 shrink-0" /> <span>Name</span>
                                    </div>
                                </th>
                                <th className="w-[20%] px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <ShieldCheck size={12} className="text-emerald-400 shrink-0" /> <span>Version</span>
                                    </div>
                                </th>
                                <th className="w-[15%] px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    Type
                                </th>
                                <th className="w-[15%] px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    Updateable
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/60 bg-gray-900/20 text-xs">
                            {loading && firmwareList.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-2">
                                            <div className="w-6 h-6 border-2 border-purple-500/20 border-t-purple-500 rounded-full animate-spin"></div>
                                            <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest">Fetching Firmware...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : firmwareList.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-2 opacity-40">
                                            <Cpu size={32} className="text-gray-500 mb-2" />
                                            <p className="text-[10px] font-bold text-gray-200 uppercase tracking-widest">No firmware data found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                firmwareList.map((fw) => (
                                    <tr 
                                        key={fw.id} 
                                        className="hover:bg-gray-800/40 transition-colors group"
                                    >
                                        <td className="px-4 py-2 whitespace-nowrap font-mono text-[10px] text-gray-200">
                                            {fw.firmware_item_id || "-"}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap">
                                            <span 
                                                className="text-[11px] font-bold text-gray-300 group-hover:text-purple-300 transition-colors block truncate"
                                                title={fw.name}
                                            >
                                                {fw.name || "-"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap font-mono text-[10px] text-gray-300">
                                            {fw.version || "-"}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap">
                                            <span className="text-[10px] font-medium text-gray-200 capitalize">
                                                {fw.item_type || "-"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap">
                                            {renderUpdateableBadge(fw.updateable)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/40 border-t border-gray-700 shrink-0 mt-auto">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => {
                                setLimit(v);
                                setCurrentPage(1);
                            }}
                        />
                        <span className="text-[9px] font-medium text-gray-500 tracking-widest">
                            <span className="text-gray-300">
                                {totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1}
                            </span>
                            {" - "}
                            <span className="text-gray-300">
                                {Math.min(currentPage * limit, totalRecords)}
                            </span>
                            <span className="mx-1 text-gray-600">/</span>
                            <span className="text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800/50 hover:bg-gray-700 rounded"
                        >
                            <ChevronsLeft size={14} />
                        </button>
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800/50 hover:bg-gray-700 rounded"
                        >
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button
                            disabled={currentPage * limit >= totalRecords}
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800/50 hover:bg-gray-700 rounded"
                        >
                            <ChevronRight size={14} />
                        </button>
                        <button
                            disabled={currentPage * limit >= totalRecords}
                            onClick={() => setCurrentPage(Math.ceil(totalRecords / limit))}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800/50 hover:bg-gray-700 rounded"
                        >
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}